import { AppCircularProgress } from '../../../../components/common/loaders/AppCircularProgress';
import {
  GoogleMap,
  Libraries,
  useLoadScript,
} from '@react-google-maps/api';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PolygonUnit from '../polygonUnit';
import {
  containerStyle,
  libraries,
  polygonType,
} from '../../../../types/mvpMap';
import { latLngConversion } from '../../../../utils/functions/maps';
import { useTiffHelper } from '../tiff';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { LegendScale } from '../../legend-scale';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface TMapProps {
  tiffFileUrls: string[]; // Changed to array
  geometryHex: string;
  options?: google.maps.MapOptions;
  indexName?: string | null;
  selectedDate?: dayjs.Dayjs; // Optional selectedDate prop
  setCenterValue?: (center: {
    lat: number;
    lng: number;
  }) => void;
  fieldIndexList: any;
  commonFilters: any;
  cloudCover: number | string;
  ndviValue: number;
  indexSelected: number | string;
  setSelectedIndex: any;
  selectedIndex: string;
}

const GoogleMapView = ({
  tiffFileUrls,
  geometryHex,
  indexName,
  selectedDate,
  setCenterValue,
  fieldIndexList,
  // commonFilters,
  cloudCover,
  ndviValue,
  // indexSelected,
  setSelectedIndex,
  selectedIndex,
}: TMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const indices = [
    {
      key: 'NDVI',
      value:
        'Normalized Difference Vegetation Index (NDVI)',
    },
    {
      key: 'CSI',
      value: 'Crop Stress Index (CSI)',
    },
    {
      key: 'EVI',
      value: 'Enhanced Vegetation Index (EVI)',
    },
    {
      key: 'LAI',
      value: 'Leaf Area Index (LAI)',
    },
    {
      key: 'VWS',
      value: 'Vegetation Water Stress (VWS)',
    },
    {
      key: 'BSI',
      value: 'Bare Soil Index (BSI)',
    },
    {
      key: 'NDWI',
      value: 'Normalized Difference Water Index (NDWI)',
    },
    {
      key: 'CHI',
      value: 'Chlorophyll Health Index (CHI)',
    },
    {
      key: 'CIG',
      value: 'Chlorophyll Index Green(CIG)',
    },
    {
      key: 'CRI700',
      value: 'Carotenoid Reflectance Index (CRI700)',
    },
    {
      key: 'CCCI',
      value: 'Canopy Chlorophyll Content Index (CCCI)',
    },
    {
      key: 'EGS',
      value: 'Early Growth Stage (EGS)',
    },
    {
      key: 'GNDVI',
      value: 'Green NDVI (GNDVI)',
    },
    {
      key: 'ARVI',
      value:
        'Atmospherically Resistant Vegetation Index (ARVI)',
    },
    {
      key: 'PVI',
      value: 'Perpendicular Vegetation Index (PVI)',
    },
    {
      key: 'MIX_LAI',
      value: 'Mixed Leaf Area Index (MIX_LAI)',
    },
    {
      key: 'ARI',
      value: 'Anthocyanin Reflectance Index (ARI)',
    },
    {
      key: 'SBI',
      value: 'Soil Brightness Index (SBI)',
    },
    {
      key: 'LLCI',
      value: 'Leaf Light Capture Index (LLCI)',
    },
    {
      key: 'VNI',
      value: 'Vegetation Nitrogen Index (VNI)',
    },
    {
      key: 'ATSAVI',
      value: 'Adjusted Transformed SAV (ATSAVI)',
    },
    {
      key: 'LWCI',
      value: 'Leaf Water Content Index (LWCI)',
    },
    {
      key: 'PPI',
      value: 'Plant Pigment Index (PPI)',
    },
    {
      key: 'CHI_VEGETABLES',
      value: 'Chlorophyll (Vegetables) (CHI_VEGETABLES)',
    },
    {
      key: 'CHI_ORCHARDS',
      value: 'Chlorophyll (Orchards) (CHI_ORCHARDS)',
    },
  ];

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env
      .REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: libraries as Libraries,
  });

  const calculatePolygonCenter = (
    polygon: polygonType[]
  ) => {
    let latSum = 0;
    let lngSum = 0;
    const numPoints = polygon?.length;
    polygon?.forEach((point) => {
      latSum += point.lat;
      lngSum += point.lng;
    });
    const centerLat = latSum / numPoints;
    const centerLng = lngSum / numPoints;
    return { lat: centerLat, lng: centerLng };
  };

  // const polygon = useMemo(
  //   () =>
  //     latLngConversion(geometryHex) as {
  //       type: string | undefined;
  //       geometry: polygonType[];
  //     },
  //   [geometryHex]
  // );
  const polygon = useMemo(() => {
    return latLngConversion(geometryHex);
  }, [geometryHex]);

  const polygonGeometry = polygon.geometry;
  const center = calculatePolygonCenter(polygonGeometry);

  useEffect(() => {
    if (polygonGeometry.length > 0) {
      const newCenter =
        calculatePolygonCenter(polygonGeometry);
      if (setCenterValue) {
        setCenterValue((prev) => {
          if (
            !prev ||
            prev.lat !== newCenter.lat ||
            prev.lng !== newCenter.lng
          ) {
            return newCenter;
          }
          return prev;
        });
      }
    }
  }, [polygonGeometry, setCenterValue]);

  const { onMapLoad } = useTiffHelper();
  const stableTiffUrls = useMemo(
    () => tiffFileUrls,
    [tiffFileUrls.join()]
  );
  useEffect(() => {
    if (!map) return;
    stableTiffUrls.forEach((url) => {
      onMapLoad(map, url, polygon.geometry, indexName);
    });
  }, [map, stableTiffUrls, indexName]);

  // useEffect(() => {
  //   if (map && tiffFileUrls.length > 0) {
  //     // Load each TIFF URL as a separate layer
  //     tiffFileUrls?.forEach((tiffFileUrl) => {
  //       onMapLoad(
  //         map,
  //         tiffFileUrl,
  //         polygon.geometry,
  //         indexName
  //       );
  //     });
  //   }
  // }, [tiffFileUrls, polygon, indexName]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleSelect = (index) => {
    setSelectedIndex(index);

    handleMenuClose();
  };

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: false,
      panControl: false,
      draggable: true,
      zoomControl: true,
      rotateControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
      mapTypeControl: false,
      disableDoubleClickZoom: true,
      mapTypeId: 'hybrid',
      mapTypeIds: [
        'satellite',
        'roadmap',
        'hybrid',
        'terrain',
      ],
      maxZoom: 19,
      minZoom: 10,
      zoomControlOptions: {
        position:
          window?.google?.maps.ControlPosition.TOP_RIGHT,
      },
    }),

    []
  );

  return (
    <>
      {!isLoaded ? (
        <AppCircularProgress />
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          options={mapOptions}
          onLoad={(map) => {
            setMap(map);

            tiffFileUrls?.forEach((tiffFileUrl) => {
              onMapLoad(
                map,
                tiffFileUrl,
                polygonGeometry,
                indexName
                //  index
              );
            });
          }}
          onUnmount={onUnmount}>
          {selectedDate && (
            <Box
              sx={{
                position: 'absolute',
                margin: '8px 12px',
                background: '#fff',
                padding: '4px 8px',
                zIndex: '1',
                borderRadius: '4px',
              }}>
              {dayjs(
                fieldIndexList?.indexTime?.seconds * 1000
              ).format('DD/MM/YYYY')}
            </Box>
          )}
          {tiffFileUrls?.[0] != undefined && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 160,
                right: 11,
                background: 'rgba(0, 0, 0, 0.55)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                // px: 1.5,
                // py: 1,
                zIndex: 3,
                color: '#fff',
                fontSize: 14,
              }}>
              {/* NDVI + Dropdown */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  //  background: 'rgba(255,255,255,0.08)',
                  //  borderRadius: '6px',
                  px: 1.2,
                  py: 0.5,
                  // border: '1px solid rgba(255,255,255,0.15)',
                }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600 }}>
                  {selectedIndex}
                </Typography>
                <Box
                  sx={{
                    background: 'rgba(2,105,93,1)',
                    borderRadius: '4px',
                    px: 0.6,
                    py: 0.2,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#fff',
                  }}>
                  {ndviValue?.toFixed(2)}
                </Box>
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{
                    color: '#fff',
                    p: 0,
                    '&:hover': {
                      background: 'transparent',
                    },
                  }}>
                  {open ? (
                    <ArrowDropDownIcon />
                  ) : (
                    <ArrowDropUpIcon />
                  )}
                </IconButton>
                {/* Drop-up Menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      background: 'rgba(0, 0, 0, 0.55)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      color: '#fff',
                      mt: -4,
                      borderRadius: '8px',
                      minWidth: 280,
                      maxHeight: 300,
                    },
                  }}>
                  {indices.map((index) => (
                    <MenuItem
                      onClick={() =>
                        handleSelect(index?.key)
                      }
                      key={index?.key}
                      sx={{
                        fontSize: 13,
                        '&:hover': {
                          background:
                            'rgba(255,255,255,0.1)',
                        },
                      }}>
                      {index?.value}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Cloud Cover */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  background: 'rgba(2,105,93,1)',
                  borderRadius: '6px',
                  px: 1.2,
                  py: 0.5,
                  border: '1px solid rgba(2,105,93,1)',
                }}>
                <CloudQueueIcon sx={{ fontSize: 12 }} />
                <Typography
                  sx={{ fontSize: 12, fontWeight: 500 }}>
                  {cloudCover}%
                </Typography>
              </Box>

              {/* Legend */}

              <LegendScale
                selectedIndex={selectedIndex}
                showData={false}
              />
            </Box>
          )}
          <PolygonUnit polygonCoords={polygonGeometry} />
        </GoogleMap>
      )}
    </>
  );
};

export default React.memo(GoogleMapView);
